import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Palette, CreditCard, UserCog, Rocket, ListChecks, X, Globe,
} from "lucide-react";
import { toast } from "sonner";

const SLUG_RE = /^[a-z0-9-]{3,32}$/;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const DOMAIN_RE = /^(?=.{1,253}$)(?!-)[a-z0-9-]{1,63}(?<!-)(?:\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/;

const STEPS = [
  { id: 1, title: "Studio", icon: Sparkles, description: "Name + slug + tagline" },
  { id: 2, title: "Branding", icon: Palette, description: "Colors + logo" },
  { id: 3, title: "Plan", icon: CreditCard, description: "Plan + currency + provider" },
  { id: 4, title: "Owner", icon: UserCog, description: "Tenant admin login" },
  { id: 5, title: "Domain", icon: Globe, description: "Custom domain (optional)" },
  { id: 6, title: "Kickstart", icon: Rocket, description: "Seed sample content" },
  { id: 7, title: "Review", icon: ListChecks, description: "Confirm and create" },
];

function passwordStrength(pwd) {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return Math.min(score, 5);
}

const empty = {
  name: "", slug: "", tagline: "",
  theme_color: "#7C3AED", accent_color: "#F59E0B", logo_url: "",
  plan: "starter", default_currency: "INR", payment_provider_preference: "mock",
  white_label_enabled: false,
  owner_full_name: "", owner_email: "", owner_password: "", owner_password_confirm: "",
  custom_domain: "",
  seed_kickstart: true,
};

export default function AdminOnboardWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: null });
  const [serverError, setServerError] = useState("");

  // default kickstart by plan
  useEffect(() => {
    if (form.plan === "enterprise") setForm((f) => ({ ...f, seed_kickstart: false }));
    if (form.plan === "starter") setForm((f) => ({ ...f, seed_kickstart: true }));
  }, [form.plan]);

  // when plan goes back to starter, force white_label off
  useEffect(() => {
    if (form.plan === "starter" && form.white_label_enabled) {
      setForm((f) => ({ ...f, white_label_enabled: false }));
    }
  }, [form.plan]);

  // live slug availability
  useEffect(() => {
    if (!form.slug) {
      setSlugStatus({ checking: false, available: null, reason: null });
      return;
    }
    if (!SLUG_RE.test(form.slug)) {
      setSlugStatus({ checking: false, available: false, reason: "format" });
      return;
    }
    setSlugStatus((s) => ({ ...s, checking: true }));
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/super-admin/tenants/check-slug?slug=${encodeURIComponent(form.slug)}`);
        setSlugStatus({ checking: false, available: !!data.available, reason: data.reason || null });
      } catch (e) {
        setSlugStatus({ checking: false, available: false, reason: "error" });
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [form.slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepValid = useMemo(() => {
    switch (step) {
      case 1:
        return !!form.name.trim() && SLUG_RE.test(form.slug) && slugStatus.available === true;
      case 2:
        return HEX_RE.test(form.theme_color) && HEX_RE.test(form.accent_color);
      case 3:
        return ["starter", "pro", "enterprise"].includes(form.plan)
          && ["INR", "USD"].includes(form.default_currency)
          && ["mock", "razorpay", "stripe"].includes(form.payment_provider_preference);
      case 4:
        return !!form.owner_full_name.trim()
          && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.owner_email)
          && form.owner_password.length >= 6
          && form.owner_password === form.owner_password_confirm;
      case 5:
        // domain is optional; if provided it must be a valid domain
        return !form.custom_domain.trim() || DOMAIN_RE.test(form.custom_domain.trim().toLowerCase());
      case 6:
        return true;
      case 7:
        return true;
      default:
        return false;
    }
  }, [step, form, slugStatus]);

  const next = () => {
    if (!stepValid) return;
    if (step < 7) setStep(step + 1);
  };
  const back = () => {
    if (step > 1) setStep(step - 1);
  };

  const submit = async () => {
    setServerError("");
    setSubmitting(true);
    try {
      const body = {
        slug: form.slug, name: form.name, tagline: form.tagline,
        owner_email: form.owner_email, owner_password: form.owner_password, owner_full_name: form.owner_full_name,
        theme_color: form.theme_color, accent_color: form.accent_color, logo_url: form.logo_url,
        plan: form.plan, default_currency: form.default_currency,
        payment_provider_preference: form.payment_provider_preference,
        white_label_enabled: form.white_label_enabled,
        seed_kickstart: form.seed_kickstart,
        custom_domain: form.custom_domain.trim().toLowerCase(),
      };
      const { data } = await api.post("/super-admin/tenants/onboard", body);
      toast.success(`${data.name} is live`);
      navigate(`/admin/tenants/${data.id}`);
    } catch (e) {
      setServerError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/admin")} className="text-white/50 hover:text-white inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em]" data-testid="onboard-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to control room
        </button>
        <button onClick={() => navigate("/admin")} className="text-white/40 hover:text-white" title="Cancel" data-testid="onboard-cancel">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Onboarding wizard</p>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Launch a new studio</h1>
        <p className="text-white/55 mt-2 max-w-xl text-sm">Seven steps. Branding, plan, owner, an optional custom domain, and a kickstart catalogue so the dashboard isn't empty on day one.</p>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-8" data-testid="onboard-stepper">
        {STEPS.map((s) => {
          const Icon = s.icon;
          const active = step === s.id;
          const done = step > s.id;
          return (
            <div
              key={s.id}
              className={`p-3 border ${active ? "border-amber-400 bg-amber-400/10" : done ? "border-emerald-400/30 bg-emerald-400/5" : "border-white/10 bg-[#121212]"}`}
              data-testid={`onboard-step-${s.id}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 grid place-items-center border ${active ? "border-amber-400 text-amber-300" : done ? "border-emerald-400/50 text-emerald-300" : "border-white/15 text-white/55"}`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">Step {s.id}</div>
              </div>
              <div className="mt-2 text-sm font-medium">{s.title}</div>
              <div className="text-[11px] text-white/40 hidden md:block">{s.description}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-[#121212] border border-white/10 p-6" data-testid="onboard-card">
        {step === 1 && (
          <Section title="Studio info" lead="Name, slug, and tagline. Slug is the URL handle (/s/your-slug).">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Studio name">
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Studio Aurora" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="onboard-name" />
              </FormField>
              <FormField label="Slug" hint="lowercase, 3–32 chars, dashes only">
                <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} placeholder="studio-aurora" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono" data-testid="onboard-slug" />
                <SlugStatus form={form} status={slugStatus} />
              </FormField>
              <FormField label="Tagline" className="md:col-span-2">
                <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Indie cinema, reimagined." className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="onboard-tagline" />
              </FormField>
            </div>
          </Section>
        )}

        {step === 2 && (
          <Section title="Branding" lead="Pick the colors that define this studio. Logo URL is optional (paste any image URL).">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Theme color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.theme_color} onChange={(e) => set("theme_color", e.target.value)} className="w-12 h-10 border border-white/15 bg-transparent" data-testid="onboard-theme-picker" />
                  <Input value={form.theme_color} onChange={(e) => set("theme_color", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="onboard-theme-input" />
                </div>
              </FormField>
              <FormField label="Accent color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="w-12 h-10 border border-white/15 bg-transparent" data-testid="onboard-accent-picker" />
                  <Input value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="onboard-accent-input" />
                </div>
              </FormField>
              <FormField label="Logo URL (optional)" className="md:col-span-2">
                <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://...png" className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono text-sm" data-testid="onboard-logo" />
              </FormField>
            </div>
            {/* Live preview */}
            <div className="mt-6 border border-white/10 p-5" style={{ background: `linear-gradient(135deg, ${form.theme_color}33, transparent)` }} data-testid="onboard-branding-preview">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10" style={{ backgroundColor: form.theme_color }} />
                <div>
                  <div className="font-display font-bold text-lg">{form.name || "Your studio"}</div>
                  <div className="text-white/65 text-sm">{form.tagline || "Tagline goes here"}</div>
                </div>
                <span className="ml-auto inline-block px-3 py-1 text-[10px] uppercase tracking-[0.25em]" style={{ backgroundColor: form.accent_color, color: "#0a0a0a" }}>Featured</span>
              </div>
            </div>
          </Section>
        )}

        {step === 3 && (
          <Section title="Plan & monetization" lead="Pick the plan + currency + payment provider preference. Real provider integration plugs in later — for MVP we use mock checkout.">
            <FormField label="Plan">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { id: "starter", title: "Starter", desc: "Branded but Vawworld-powered. No white label." },
                  { id: "pro", title: "Pro", desc: "White label. Full branding control." },
                  { id: "enterprise", title: "Enterprise", desc: "White label + future custom domains." },
                ].map((p) => {
                  const active = form.plan === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => set("plan", p.id)}
                      className={`text-left p-4 border transition-colors ${active ? "border-amber-400 bg-amber-400/10" : "border-white/15 hover:border-white/40"}`}
                      data-testid={`onboard-plan-${p.id}`}
                    >
                      <div className="font-display font-bold text-base uppercase tracking-wider">{p.title}</div>
                      <div className="text-white/55 text-xs mt-1">{p.desc}</div>
                    </button>
                  );
                })}
              </div>
            </FormField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <FormField label="Default currency">
                <div className="flex gap-2">
                  {["INR", "USD"].map((c) => (
                    <button key={c} type="button" onClick={() => set("default_currency", c)}
                      className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] border ${form.default_currency === c ? "bg-white text-black border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}
                      data-testid={`onboard-currency-${c}`}
                    >{c}</button>
                  ))}
                </div>
              </FormField>
              <FormField label="Payment provider">
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: "mock", label: "Mock" },
                    { id: "razorpay", label: "Razorpay" },
                    { id: "stripe", label: "Stripe" },
                  ].map((p) => (
                    <button key={p.id} type="button" onClick={() => set("payment_provider_preference", p.id)}
                      className={`px-4 py-2 text-[11px] uppercase tracking-[0.25em] border ${form.payment_provider_preference === p.id ? "bg-white text-black border-white" : "border-white/15 text-white/55 hover:border-white/40"}`}
                      data-testid={`onboard-provider-${p.id}`}
                    >{p.label}</button>
                  ))}
                </div>
              </FormField>
            </div>
            <FormField label="White label" className="mt-4">
              <div className="flex items-center gap-3 h-10">
                <Switch
                  checked={form.white_label_enabled}
                  onCheckedChange={(v) => set("white_label_enabled", v)}
                  disabled={form.plan === "starter"}
                  data-testid="onboard-white-label"
                />
                <span className="text-sm text-white/65">
                  {form.plan === "starter" ? "Available on Pro+" : (form.white_label_enabled ? "Powered-by line hidden" : "Powered by Vawworld shown")}
                </span>
              </div>
            </FormField>
          </Section>
        )}

        {step === 4 && (
          <Section title="Owner account" lead="The tenant admin who will manage this studio. They'll be able to log in immediately at /login.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Full name" className="md:col-span-2">
                <Input value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} placeholder="Aurora Admin" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="onboard-owner-name" />
              </FormField>
              <FormField label="Email" className="md:col-span-2">
                <Input type="email" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value.toLowerCase())} placeholder="admin@studio.com" className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="onboard-owner-email" />
              </FormField>
              <FormField label="Password (≥6 chars)">
                <Input type="password" value={form.owner_password} onChange={(e) => set("owner_password", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="onboard-owner-password" />
                <PasswordStrength password={form.owner_password} />
              </FormField>
              <FormField label="Confirm password">
                <Input type="password" value={form.owner_password_confirm} onChange={(e) => set("owner_password_confirm", e.target.value)} className="bg-transparent border-white/15 rounded-none focus-visible:border-white" data-testid="onboard-owner-password-confirm" />
                {form.owner_password && form.owner_password_confirm && form.owner_password !== form.owner_password_confirm && (
                  <div className="text-[10px] text-rose-300 mt-1">Passwords don't match</div>
                )}
              </FormField>
            </div>
          </Section>
        )}

        {step === 5 && (
          <Section title="Custom domain" lead="Optional — put this studio on its own domain (e.g. idlichutnee.com). You can also skip and add it later from Studio → Domains.">
            <FormField label="Domain" hint="Leave blank to skip. Enter a domain you own, without https:// or www.">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 border border-white/15 grid place-items-center text-white/50 shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
                <Input
                  value={form.custom_domain}
                  onChange={(e) => set("custom_domain", e.target.value.toLowerCase().trim())}
                  placeholder="idlichutnee.com"
                  className="bg-transparent border-white/15 rounded-none focus-visible:border-white font-mono"
                  data-testid="onboard-domain"
                />
              </div>
              {form.custom_domain.trim() && !DOMAIN_RE.test(form.custom_domain.trim()) && (
                <div className="text-[10px] text-rose-300 mt-1" data-testid="onboard-domain-error">Enter a valid domain like idlichutnee.com</div>
              )}
              {form.custom_domain.trim() && DOMAIN_RE.test(form.custom_domain.trim()) && (
                <div className="text-[10px] text-emerald-300 mt-1" data-testid="onboard-domain-ok">Looks good — we&apos;ll generate DNS records after launch</div>
              )}
            </FormField>

            <div className="mt-5 border border-white/10 bg-[#0a0a0a] p-4">
              <div className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
                <div className="text-xs text-white/55 leading-relaxed">
                  <p className="text-white/75 font-medium mb-1">How it works</p>
                  We connect the domain now (status: <span className="text-amber-300">pending</span>). After launch, the studio
                  owner opens <span className="text-white">Studio → Domains</span>, adds the CNAME + TXT records we generate,
                  and clicks Verify. Once verified, the storefront goes live on the domain — no <code className="text-white/60">/s/</code> path.
                </div>
              </div>
            </div>
          </Section>
        )}

        {step === 6 && (
          <Section title="Kickstart content" lead="Seed 3 sample movies + 3 sample reels into this studio's catalogue so the dashboard isn't empty on day one. Different tenants get different rotations.">
            <div
              role="button"
              tabIndex={0}
              onClick={() => set("seed_kickstart", !form.seed_kickstart)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && set("seed_kickstart", !form.seed_kickstart)}
              className={`w-full text-left p-5 border cursor-pointer ${form.seed_kickstart ? "border-emerald-400/40 bg-emerald-400/5" : "border-white/10 bg-[#0a0a0a]"}`}
              data-testid="onboard-kickstart-toggle"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Rocket className={`w-5 h-5 ${form.seed_kickstart ? "text-emerald-300" : "text-white/45"}`} />
                  <div>
                    <div className="font-display font-bold text-base">{form.seed_kickstart ? "Kickstart enabled" : "Kickstart disabled"}</div>
                    <div className="text-white/55 text-xs mt-0.5">
                      {form.seed_kickstart
                        ? "We'll seed 3 published movies + 3 reels (CC-licensed Blender shorts)."
                        : "Tenant starts with an empty catalogue."}
                    </div>
                  </div>
                </div>
                <Switch checked={form.seed_kickstart} onCheckedChange={(v) => set("seed_kickstart", v)} data-testid="onboard-kickstart-switch" />
              </div>
            </div>
          </Section>
        )}

        {step === 7 && (
          <Section title="Review & launch" lead="Make sure everything looks right. The wizard rolls back partial inserts on failure.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid="onboard-review">
              <ReviewBlock label="Studio">
                <div className="font-medium">{form.name}</div>
                <div className="text-white/55 font-mono">/s/{form.slug}</div>
                <div className="text-white/45">{form.tagline || "—"}</div>
              </ReviewBlock>
              <ReviewBlock label="Branding">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 inline-block" style={{ backgroundColor: form.theme_color }} />
                  <span className="font-mono text-xs">{form.theme_color}</span>
                  <span className="w-5 h-5 inline-block ml-2" style={{ backgroundColor: form.accent_color }} />
                  <span className="font-mono text-xs">{form.accent_color}</span>
                </div>
              </ReviewBlock>
              <ReviewBlock label="Plan & monetization">
                <div className="capitalize">{form.plan} · {form.default_currency} · provider: {form.payment_provider_preference}</div>
                <div className="text-white/55">White label: {form.white_label_enabled ? "On" : "Off"}</div>
              </ReviewBlock>
              <ReviewBlock label="Owner">
                <div className="font-medium">{form.owner_full_name}</div>
                <div className="text-white/55">{form.owner_email}</div>
              </ReviewBlock>
              <ReviewBlock label="Custom domain">
                {form.custom_domain.trim()
                  ? <div className="font-mono text-white/80">{form.custom_domain.trim()} <span className="text-amber-300 font-sans not-italic">· pending</span></div>
                  : <div className="text-white/45">None — can add later in Studio → Domains</div>}
              </ReviewBlock>
              <ReviewBlock label="Kickstart" className="md:col-span-2">
                <div>{form.seed_kickstart ? "Seed 3 movies + 3 reels (CC-licensed)." : "Empty catalogue."}</div>
              </ReviewBlock>
            </div>
            {serverError && <div className="mt-4 text-sm text-rose-300 border border-rose-400/30 bg-rose-500/5 p-3" data-testid="onboard-error">{serverError}</div>}
          </Section>
        )}

        <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
          <Button onClick={back} disabled={step === 1 || submitting} variant="ghost" className="rounded-none uppercase text-[11px] tracking-[0.25em] text-white/60 hover:text-white" data-testid="onboard-prev-button">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
          </Button>
          {step < 7 ? (
            <Button onClick={next} disabled={!stepValid} className="rounded-none bg-white text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="onboard-next-button">
              Next <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="rounded-none bg-amber-400 text-black hover:bg-amber-300 uppercase text-[11px] tracking-[0.25em]" data-testid="onboard-submit-button">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Rocket className="w-3.5 h-3.5 mr-1.5" /> Launch studio</>)}
            </Button>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Section({ title, lead, children }) {
  return (
    <div>
      <h2 className="font-display font-bold text-2xl tracking-tight">{title}</h2>
      <p className="text-white/55 text-sm mt-1 max-w-xl mb-5">{lead}</p>
      {children}
    </div>
  );
}

function FormField({ label, hint, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
      {hint && <div className="text-[10px] text-white/30">{hint}</div>}
    </div>
  );
}

function SlugStatus({ form, status }) {
  if (!form.slug) return null;
  if (status.checking) {
    return <div className="text-[10px] text-white/40 mt-1">Checking…</div>;
  }
  if (status.available === true) {
    return <div className="text-[10px] text-emerald-300 mt-1" data-testid="slug-available">Available — /s/{form.slug}</div>;
  }
  if (status.reason === "format") {
    return <div className="text-[10px] text-rose-300 mt-1" data-testid="slug-format-error">Slug must match ^[a-z0-9-]&#123;3,32&#125;$</div>;
  }
  if (status.reason === "taken") {
    return <div className="text-[10px] text-rose-300 mt-1" data-testid="slug-taken">Already taken — pick another</div>;
  }
  return <div className="text-[10px] text-white/40 mt-1">—</div>;
}

function PasswordStrength({ password }) {
  if (!password) return null;
  const score = passwordStrength(password);
  const colors = ["bg-rose-500", "bg-rose-400", "bg-amber-400", "bg-amber-300", "bg-emerald-400", "bg-emerald-300"];
  const labels = ["Way too weak", "Weak", "OK", "Good", "Strong", "Excellent"];
  return (
    <div className="mt-2" data-testid="password-strength">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`h-1 flex-1 ${i <= score ? colors[score] : "bg-white/10"}`} />
        ))}
      </div>
      <div className="text-[10px] text-white/45 mt-1">{labels[score]}</div>
    </div>
  );
}

function ReviewBlock({ label, children, className = "" }) {
  return (
    <div className={`border border-white/10 bg-[#0a0a0a] p-4 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
      <div className="mt-2 space-y-1">{children}</div>
    </div>
  );
}
